'use client';

import { getProductContentTexts } from '@eliadmin/core/src/utils/project-content-text';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { isNullish, youtubeEmbedToNotEmbed } from '@eliadmin/core/src/utils/misc-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import { useState } from 'react';
import { FaYoutube } from 'react-icons/fa';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function ProductHeader() {
  const { t } = useTranslations();
  const descriptionLength = 200;
  const product = useVariantsStore(s => s.product);
  const videoTutorial =
    getProductContentTexts(product)?.find((tf: any) => tf.id === 2)?.value ?? null;

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={cn('flex flex-col gap-3', 'ea-product-detail-header')}>
      {/* tags */}
      <div className={cn('flex flex-row gap-2', 'ea-product-detail-header__badge-wrapper')}>
        {product.tags?.map((badge, i) => {
          return (
            <Badge
              key={`tag-${i}`}
              style={{ backgroundColor: badge.color, color: getContrastYIQ(badge.color) }}
              size="md"
              className={cn('flex flex-row gap-2', 'ea-product-detail-header__badge')}
            >
              {badge.name}
            </Badge>
            // <span
            //   key={`tag-${i}`}
            //   className={'mr-2 rounded-2xl px-2 py-1 text-xs'}
            //   style={{ backgroundColor: badge.color, color: getContrastYIQ(badge.color) }}
            // >
            //   {badge.name}
            // </span>
          );
        })}
      </div>
      {/* title */}
      <h1 className={cn('mb-0 text-2xl font-bold', 'ea-product-detail-header__title')}>
        {product.name}
      </h1>
      <div className={cn('flex flex-row gap-4', 'ea-product-detail-header__content')}>
        {/* rating */}
        {!isNullish(product.rating) && (
          <ProductRating
            className={{
              wrapper: cn('', 'ea-product-detail-header__product-rating-wrapper'),
              starIcon: cn(
                'fill-rating stroke-rating',
                'ea-product-detail-header__product-rating-star-icon',
              ),
              starWrapper: cn('', 'ea-product-detail-header__product-rating-star-wrapper'),
              starDisabled: cn(
                'fill-white stroke-white',
                'ea-product-detail-header__product-rating-star-disabled',
              ),
            }}
            rating={product.rating}
          />
        )}
        {/* video */}
        {product.video && (
          <Button
            variant="outline"
            className={cn(
              'ml-4 flex h-8 cursor-pointer items-center space-x-1 px-4 py-0',
              'ea-product-detail-header__product-video',
            )}
            id="showYtbVideo"
            onClick={() => {
              window.open(youtubeEmbedToNotEmbed(product.video), '_blank');
            }}
          >
            <FaYoutube
              className={cn(
                'h-[18px] w-[18px] fill-red-500',
                'ea-product-detail-header__product-video-youtube',
              )}
            />
            <p
              className={cn(
                'text-xs font-semibold text-foreground',
                'ea-product-detail-header__product-video-text',
              )}
            >
              {t('text451')}
            </p>
          </Button>
        )}
        {!!videoTutorial && (
          <Button asChild className="flex h-8 cursor-pointer items-center text-xs">
            <Link href={videoTutorial}>{t('text1147')}</Link>
          </Button>
        )}
      </div>
      {/* description */}
      <div
        className={cn(
          'mt-2 flex flex-col items-start justify-start',
          'ea-product-detail-header__product-description',
        )}
      >
        <HtmlRendererRaw
          className={cn(
            'ea-product-detail-header__product-description-content text-ellipsis',
            !isExpanded &&
              (product.short_description || product.description).length > descriptionLength &&
              'line-clamp-2',
          )}
          html={product.short_description || product.description}
        />
        {(product.short_description || product.description).length > descriptionLength && (
          <Button
            asChild
            variant={'link'}
            className={cn(
              'mt-2 h-6 flex-row items-center space-x-1 p-0 underline hover:text-muted-foreground',
              'ea-product-detail-header__product-description-button',
            )}
          >
            <a
              href="#product-tab-description"
              className="no-underline hover:text-primary hover:no-underline"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {!isExpanded ? (
                <>
                  {t('text529')}
                  <FiChevronDown className={cn('h-5 w-5')} />
                </>
              ) : (
                <>
                  {t('text528')}
                  <FiChevronUp className={cn('h-5 w-5')} />
                </>
              )}
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}
